// Blind Coding Tab Component
import { useState, useEffect } from "react";
import axios from "axios";
import {
  EyeOff,
  Eye,
  Send,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Users,
  Database,
  ExternalLink,
  ShieldCheck
} from "lucide-react";
import { Badge } from "../common";
import { API } from "../../lib/constants";

// Blind Coding Form Component
const BlindCodingForm = ({ onSubmit, loading }) => {
  const [orientation, setOrientation] = useState("");
  const [governance, setGovernance] = useState("");
  const [confidence, setConfidence] = useState("medium");
  const [notes, setNotes] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!orientation || !governance) {
      alert("Please select both orientation and governance framing");
      return;
    }
    onSubmit(orientation, governance, confidence, notes);
    // Reset form
    setOrientation("");
    setGovernance("");
    setNotes("");
  };

  const orientationOptions = [
    { value: "care", label: "Care-Centered", desc: "Health, education, wellbeing, accessibility", color: "emerald" },
    { value: "utility", label: "Utility-Centered", desc: "Productivity, automation, efficiency", color: "blue" },
    { value: "creative", label: "Creative", desc: "Art, music, video, design tools", color: "violet" },
    { value: "extraction", label: "Profit/Extraction", desc: "Growth hacking, engagement loops", color: "red" }
  ];

  const governanceOptions = [
    { value: "system_aware", label: "System-Aware", desc: "Mentions safety, ethics, consent", color: "emerald" },
    { value: "neutral", label: "Neutral", desc: "Technical focus, no ethical stance", color: "zinc" },
    { value: "market_first", label: "Market-First", desc: "Growth-dominant messaging", color: "amber" }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Orientation Selection */}
      <div>
        <label className="block text-sm font-semibold mb-3">Value Orientation</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {orientationOptions.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setOrientation(opt.value)}
              data-testid={`orientation-${opt.value}`}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                orientation === opt.value 
                  ? `border-${opt.color}-500 bg-${opt.color}-500/10` 
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <p className="font-semibold text-sm">{opt.label}</p>
              <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Governance Selection */}
      <div>
        <label className="block text-sm font-semibold mb-3">Governance Framing</label>
        <div className="grid grid-cols-3 gap-3">
          {governanceOptions.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setGovernance(opt.value)}
              data-testid={`governance-${opt.value}`}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                governance === opt.value 
                  ? `border-${opt.color}-500 bg-${opt.color}-500/10` 
                  : 'border-border hover:border-muted-foreground/50'
              }`}
            >
              <p className="font-semibold text-sm">{opt.label}</p>
              <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Confidence & Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold mb-2">Confidence Level</label>
          <select
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg"
            data-testid="confidence-select"
          >
            <option value="low">Low - Unsure</option>
            <option value="medium">Medium - Reasonably confident</option>
            <option value="high">High - Very confident</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Notes (optional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any observations..."
            className="w-full px-3 py-2 bg-background border border-border rounded-lg"
            data-testid="notes-input"
          />
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || !orientation || !governance}
        data-testid="submit-blind-coding-btn"
        className="w-full py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            Submit Classification
          </>
        )}
      </button>
    </form>
  );
};

export const BlindCodingTab = ({ showNotification }) => {
  const [blindCodingQueue, setBlindCodingQueue] = useState([]);
  const [blindCodingStats, setBlindCodingStats] = useState(null);
  const [currentBlindApp, setCurrentBlindApp] = useState(null);
  const [blindCodingIndex, setBlindCodingIndex] = useState(0);
  const [coderId, setCoderId] = useState(() => localStorage.getItem('aioo_coder_id') || '');
  const [blindCodingLoading, setBlindCodingLoading] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [revealData, setRevealData] = useState(null);

  // Fetch blind coding queue
  const fetchBlindCodingQueue = async () => {
    setBlindCodingLoading(true);
    try {
      const params = coderId ? { coder_id: coderId, limit: 10 } : { limit: 10 };
      const res = await axios.get(`${API}/blind-coding/queue`, { params });
      setBlindCodingQueue(res.data);
      if (res.data.length > 0) {
        setCurrentBlindApp(res.data[0]);
        setBlindCodingIndex(0);
      } else {
        setCurrentBlindApp(null);
      }
    } catch (error) {
      void 0; // error handled silently
      showNotification("Error loading blind coding queue", "error");
    } finally {
      setBlindCodingLoading(false);
    }
  };

  // Fetch blind coding stats
  const fetchBlindCodingStats = async () => {
    try {
      const res = await axios.get(`${API}/blind-coding/stats`);
      setBlindCodingStats(res.data);
    } catch (error) {
      void 0; // error handled silently
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchBlindCodingQueue();
    fetchBlindCodingStats();
  }, []);

  // Submit blind coding
  const submitBlindCoding = async (orientation, governance, confidence = "medium", notes = "") => {
    if (!currentBlindApp) return;
    
    setBlindCodingLoading(true);
    try {
      // Save coder ID to localStorage
      if (coderId) {
        localStorage.setItem('aioo_coder_id', coderId);
      }
      
      await axios.post(`${API}/blind-coding/submit`, {
        app_id: currentBlindApp.id,
        value_orientation: orientation,
        governance_framing: governance,
        confidence,
        notes,
        coder_id: coderId || undefined
      });
      
      showNotification("Classification submitted successfully!", "success");
      
      // Fetch reveal data to show comparison
      const revealRes = await axios.get(`${API}/blind-coding/compare/${currentBlindApp.id}`);
      setRevealData(revealRes.data);
      setShowReveal(true);
      
      // Refresh stats
      fetchBlindCodingStats();
      
    } catch (error) {
      void 0; // error handled silently
      showNotification(error.response?.data?.detail || "Error submitting classification", "error");
    } finally {
      setBlindCodingLoading(false);
    }
  };

  // Move to next app in blind coding queue
  const nextBlindCodingApp = () => {
    setShowReveal(false);
    setRevealData(null);
    
    if (blindCodingIndex < blindCodingQueue.length - 1) {
      setBlindCodingIndex(prev => prev + 1);
      setCurrentBlindApp(blindCodingQueue[blindCodingIndex + 1]);
    } else {
      // Fetch more apps
      fetchBlindCodingQueue();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-violet-500/20 rounded-lg">
            <EyeOff className="w-8 h-8 text-violet-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Blind Coding Mode</h2>
            <p className="text-muted-foreground">
              Classify AI app orientations <strong>without seeing founder demographics</strong>. 
              This eliminates unconscious bias and ensures coding decisions are based purely on product characteristics.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Panel */}
      {blindCodingStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-muted-foreground text-sm">Apps Blind-Coded</p>
            <p className="text-2xl font-bold text-violet-400">
              {blindCodingStats.progress.apps_with_blind_coding}
              <span className="text-sm text-muted-foreground font-normal ml-1">
                / {blindCodingStats.progress.total_apps}
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {blindCodingStats.progress.coverage_percent}% coverage
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-muted-foreground text-sm">Total Codings</p>
            <p className="text-2xl font-bold">{blindCodingStats.progress.total_blind_codings}</p>
            <p className="text-xs text-muted-foreground mt-1">
              By {blindCodingStats.progress.unique_coders} unique coders
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-muted-foreground text-sm">Agreement Rate</p>
            <p className={`text-2xl font-bold ${
              blindCodingStats.quality.agreement_rate_percent > 80 ? 'text-emerald-400' :
              blindCodingStats.quality.agreement_rate_percent > 60 ? 'text-amber-400' : 'text-red-400'
            }`}>
              {blindCodingStats.quality.agreement_rate_percent}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Blind vs. original match
            </p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-muted-foreground text-sm">Bias Status</p>
            <div className="flex items-center gap-2 mt-1">
              <ShieldCheck className={`w-5 h-5 ${
                blindCodingStats.quality.agreement_rate_percent > 80 ? 'text-emerald-400' :
                blindCodingStats.quality.agreement_rate_percent > 60 ? 'text-amber-400' : 'text-red-400'
              }`} />
              <p className="text-sm font-medium">
                {blindCodingStats.quality.agreement_rate_percent > 80 ? 'Low Bias' :
                 blindCodingStats.quality.agreement_rate_percent > 60 ? 'Some Bias' : 'Check Needed'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Coder ID Input */}
      <div className="bg-card border border-border rounded-lg p-4">
        <label className="block text-sm font-medium mb-2">Your Coder ID (optional, for tracking)</label>
        <input
          type="text"
          value={coderId}
          onChange={(e) => setCoderId(e.target.value)}
          placeholder="Enter a unique identifier (e.g., your initials)"
          className="w-full md:w-64 px-3 py-2 bg-background border border-border rounded-lg text-sm"
          data-testid="coder-id-input"
        />
        <p className="text-xs text-muted-foreground mt-1">Used to track your contributions and prevent duplicate codings</p>
      </div>

      {/* Coding Interface */}
      {!showReveal ? (
        <div className="bg-card border border-border rounded-xl p-6">
          {blindCodingLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading app for classification...</p>
            </div>
          ) : currentBlindApp ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Classify This App</h3>
                <span className="text-sm text-muted-foreground">
                  App {blindCodingIndex + 1} of {blindCodingQueue.length}
                </span>
              </div>

              {/* App Details (NO DEMOGRAPHIC INFO) */}
              <div className="bg-muted/30 rounded-lg p-5 border border-border">
                <h4 className="text-xl font-bold mb-2">{currentBlindApp.product_name}</h4>
                <p className="text-lg text-primary mb-4">{currentBlindApp.tagline}</p>
                {currentBlindApp.description && (
                  <p className="text-muted-foreground mb-4">{currentBlindApp.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>Launched: {currentBlindApp.launch_date?.substring(0, 10)}</span>
                  {currentBlindApp.website_url && (
                    <a 
                      href={currentBlindApp.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-primary hover:underline"
                    >
                      Visit Website <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              {/* Classification Form */}
              <BlindCodingForm onSubmit={submitBlindCoding} loading={blindCodingLoading} />
            </div>
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
              <h3 className="text-xl font-semibold mb-2">All caught up!</h3>
              <p className="text-muted-foreground mb-4">You've classified all available apps.</p>
              <button
                onClick={fetchBlindCodingQueue}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Check for More
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Reveal Panel - Shows after submission */
        <div className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Eye className="w-6 h-6 text-violet-400" />
            <h3 className="text-lg font-semibold">Classification Comparison</h3>
          </div>

          {revealData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Your Classification */}
                <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <EyeOff className="w-4 h-4" /> Your Blind Classification
                  </h4>
                  {revealData.blind_codings?.length > 0 && (
                    <div className="space-y-2">
                      <p>Orientation: <Badge type={revealData.blind_codings[revealData.blind_codings.length-1].value_orientation} variant="orientation" /></p>
                      <p>Governance: <Badge type={revealData.blind_codings[revealData.blind_codings.length-1].governance_framing} variant="governance" /></p>
                    </div>
                  )}
                </div>

                {/* Original Classification */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Database className="w-4 h-4" /> Original Classification
                  </h4>
                  <div className="space-y-2">
                    <p>Orientation: <Badge type={revealData.original_classification?.value_orientation} variant="orientation" /></p>
                    <p>Governance: <Badge type={revealData.original_classification?.governance_framing} variant="governance" /></p>
                  </div>
                </div>
              </div>

              {/* Revealed Demographics */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Revealed Demographics
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Founder Composition</p>
                    <Badge type={revealData.revealed_demographics?.founder_composition} variant="founder" />
                  </div>
                  {revealData.revealed_demographics?.founder_names && (
                    <div>
                      <p className="text-sm text-muted-foreground">Founder Names</p>
                      <p className="font-medium">{revealData.revealed_demographics.founder_names}</p>
                    </div>
                  )}
                  {revealData.revealed_demographics?.country && (
                    <div>
                      <p className="text-sm text-muted-foreground">Country</p>
                      <p className="font-medium">{revealData.revealed_demographics.country}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Agreement Status */}
              <div className={`rounded-lg p-4 ${
                revealData.agreement ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
              }`}>
                <div className="flex items-center gap-3">
                  {revealData.agreement ? (
                    <>
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                      <div>
                        <p className="font-semibold text-emerald-400">Classifications Match!</p>
                        <p className="text-sm text-muted-foreground">Your blind coding agrees with the original classification.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-6 h-6 text-amber-400" />
                      <div>
                        <p className="font-semibold text-amber-400">Classifications Differ</p>
                        <p className="text-sm text-muted-foreground">Your classification differs from the original. This helps identify potential bias in the original coding.</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button
                onClick={nextBlindCodingApp}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center justify-center gap-2"
                data-testid="next-blind-app-btn"
              >
                Next App <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlindCodingTab;
